import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Rect, Line } from 'react-native-svg';
import { FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, Toast, SingleDatePicker } from '../../components';
import { computePortalPosition } from '../../utils/portalPosition';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { talentSpecializationsApi, talentDashboardApi, organizationsApi } from '../../services/apiService';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { createShadow } from '../../utils/styles';

type NavProp = NativeStackNavigationProp<MainStackParamList>;

/* ── Constants ──────────────────────────────────────────────── */
const FOREGROUND = '#414042';
const BORDER = '#E2E8F0';
const SECTION_BG = '#F1F5F9';
const MUTED = '#64748B';
const BRAND_RED = '#A9211F';
const INPUT_BG = '#FFFFFF';

/* ── Types ───────────────────────────────────────────────────── */
interface DaySchedule {
  day: string;
  labelAr: string;
  labelEn: string;
  selected: boolean;
  startTime: string;
  endTime: string;
}

const DEFAULT_DAYS: DaySchedule[] = [
  { day: 'sat', labelAr: 'السبت', labelEn: 'Sat', selected: false, startTime: '', endTime: '' },
  { day: 'sun', labelAr: 'الأحد', labelEn: 'Sun', selected: false, startTime: '', endTime: '' },
  { day: 'mon', labelAr: 'الاثنين', labelEn: 'Mon', selected: false, startTime: '', endTime: '' },
  { day: 'tue', labelAr: 'الثلاثاء', labelEn: 'Tue', selected: false, startTime: '', endTime: '' },
  { day: 'wed', labelAr: 'الأربعاء', labelEn: 'Wed', selected: false, startTime: '', endTime: '' },
  { day: 'thu', labelAr: 'الخميس', labelEn: 'Thu', selected: false, startTime: '', endTime: '' },
  { day: 'fri', labelAr: 'الجمعة', labelEn: 'Fri', selected: false, startTime: '', endTime: '' },
];

interface SpecializationItem {
  id: number | null; // null = new
  name: string;
  description: string;
  location: string;
  startDate: string;
  endDate: string;
  budget: string;
  days: DaySchedule[];
  department_id: number | null;
  isExpanded: boolean;
  isPublished: boolean;
}

/* ── Icons ───────────────────────────────────────────────────── */
function ChevronDownIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M6 9l6 6 6-6" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function ChevronUpIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M18 15l-6-6-6 6" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function TrashIcon({ size = 16, color = '#EF4444' }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function DragHandleIcon({ size = 16, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Line x1="4" y1="8" x2="20" y2="8" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
      <Line x1="4" y1="12" x2="20" y2="12" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
      <Line x1="4" y1="16" x2="20" y2="16" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    </Svg>
  );
}

function UploadIcon({ size = 20, color = MUTED }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function CalendarIcon({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/* ── Date Picker Helpers ────────────────────────────────────── */
const DP_AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const DP_EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DP_AR_DAYS = ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب'];
const DP_EN_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function buildMonthCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  return cells;
}

/* ── Legacy in-file date picker — superseded by the shared components/SingleDatePicker
   (year support + viewport-aware placement). Kept dead to minimise churn; unused. */
function _LegacySingleDatePicker({ value, onChange, placeholder, minDate, maxDate }: { value: string; onChange: (date: string) => void; placeholder?: string; minDate?: string; maxDate?: string }) {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const btnRef = useRef<View>(null);
  const initMonth = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initMonth.getMonth());
  const todayStr = useMemo(() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`; }, []);

  const handleOpen = () => {
    if (isOpen) { setIsOpen(false); return; }
    const d = value ? new Date(value) : new Date();
    setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => { setLayout({ x, y, width: w, height: h }); setIsOpen(true); });
  };

  useEffect(() => { if (!isOpen) return; const h = () => setIsOpen(false); window.addEventListener('scroll', h, true); return () => window.removeEventListener('scroll', h, true); }, [isOpen]);

  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); } else setViewMonth(viewMonth - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); } else setViewMonth(viewMonth + 1); };
  const handleSelect = (dateStr: string) => { onChange(dateStr); setIsOpen(false); };

  const months = isRTL ? DP_AR_MONTHS : DP_EN_MONTHS;
  const dayHeaders = isRTL ? DP_AR_DAYS : DP_EN_DAYS;
  const cells = buildMonthCells(viewYear, viewMonth);
  const displayValue = value ? (() => { const [y, m, d] = value.split('-'); return `${d}/${m}/${y}`; })() : '';

  const portalContent = isOpen && Platform.OS === 'web' ? ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }} onClick={() => setIsOpen(false)} />
      <div style={{ position: 'fixed', top: layout.y + layout.height + 4, left: Math.max(8, layout.x + layout.width - 320), zIndex: 99999 }}>
        <View style={[dpStyles.panel, { backgroundColor: isDark ? colors.card : '#FFFFFF', borderColor: isDark ? colors.border : BORDER }]}>
          <View style={dpStyles.monthNav}>
            <TouchableOpacity onPress={nextMonth} style={dpStyles.navBtn}><ChevronDownIcon size={14} color={FOREGROUND} /></TouchableOpacity>
            <Text style={[dpStyles.monthLabel, { color: FOREGROUND }]}>{months[viewMonth]} {viewYear}</Text>
            <TouchableOpacity onPress={prevMonth} style={dpStyles.navBtn}><ChevronUpIcon size={14} color={FOREGROUND} /></TouchableOpacity>
          </View>
          <View style={dpStyles.dayHeaderRow}>{dayHeaders.map(h => (<Text key={h} style={[dpStyles.dayHeaderText, { color: MUTED }]}>{h}</Text>))}</View>
          <View style={dpStyles.grid}>
            {cells.map((cell, idx) => {
              const isSelected = cell.dateStr === value;
              const isToday = cell.dateStr === todayStr;
              const isDisabled = !cell.isCurrentMonth || (minDate ? cell.dateStr < minDate : false) || (maxDate ? cell.dateStr > maxDate : false);
              return (
                <TouchableOpacity key={idx} style={[dpStyles.cell, isSelected && { backgroundColor: BRAND_RED, borderRadius: 20 }, isToday && !isSelected && { backgroundColor: '#F1F5F9', borderRadius: 20 }]} onPress={() => !isDisabled && handleSelect(cell.dateStr)} disabled={isDisabled}>
                  <Text style={[dpStyles.cellText, { color: isDisabled ? '#CBD5E1' : FOREGROUND }, isSelected && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{cell.day}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <View ref={btnRef}>
      <TouchableOpacity onPress={handleOpen} style={[dpStyles.button, { backgroundColor: INPUT_BG, borderColor: BORDER }]}>
        <CalendarIcon size={16} color={MUTED} />
        <Text style={[dpStyles.buttonText, { color: value ? FOREGROUND : MUTED }]}>{displayValue || placeholder || (isRTL ? 'اختر التاريخ' : 'Select date')}</Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
}

/* ── Inject CSS for time picker selects ─────────────────────── */
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const id = 'edit-talents-time-picker-no-arrow';
  if (!document.getElementById(id)) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = '.tp-select { -webkit-appearance: none !important; -moz-appearance: none !important; appearance: none !important; background-image: none !important; }';
    document.head.appendChild(s);
  }
}

/* ── 12-hour Time Picker (AM/PM) ────────────────────────────── */
function TimePicker12h({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const parse = (v: string) => {
    if (!v) return { h: '', m: '', p: 'AM' };
    const [hh, mm] = v.split(':');
    let h24 = parseInt(hh || '0', 10);
    const p = h24 >= 12 ? 'PM' : 'AM';
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    return { h: String(h12), m: mm || '00', p };
  };
  const { h, m, p } = parse(value);

  const update = (newH: string, newM: string, newP: string) => {
    let h24 = parseInt(newH || '12', 10);
    if (newP === 'PM' && h24 !== 12) h24 += 12;
    if (newP === 'AM' && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, '0')}:${newM || '00'}`);
  };

  const selectStyle: any = {
    border: 'none', outline: 'none', backgroundColor: 'transparent',
    fontSize: 14, fontFamily: 'inherit', cursor: 'pointer',
    padding: '4px 2px', textAlign: 'center',
    WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, direction: 'ltr', width: '100%', justifyContent: 'flex-end' }}>
      <select className="tp-select" value={h || '12'} onChange={(e) => update(e.target.value, m, p)} style={selectStyle}>
        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(hh => (
          <option key={hh} value={String(Number(hh))}>{hh}</option>
        ))}
      </select>
      <span style={{ fontSize: 14, color: MUTED }}>:</span>
      <select className="tp-select" value={m} onChange={(e) => update(h, e.target.value, p)} style={selectStyle}>
        {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(mm => (
          <option key={mm} value={mm}>{mm}</option>
        ))}
      </select>
      <select className="tp-select" value={p} onChange={(e) => update(h, m, e.target.value)} style={{ ...selectStyle, fontWeight: 600, color: BRAND_RED }}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

/* ── Custom Dropdown Component ───────────────────────────────── */
function CustomDropdown({ value, options, onChange, placeholder }: {
  value: number | null;
  options: { id: number; name: string }[];
  onChange: (id: number | null) => void;
  placeholder?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const btnRef = useRef<View>(null);
  const selectedLabel = options.find(o => o.id === value)?.name || '';

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    if (isOpen) { setIsOpen(false); return; }
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setLayout({ x, y, width: w, height: h });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web') return;
    // Reposition the list to follow the trigger on scroll/resize instead of
    // closing it (the old behaviour hid the popup for the last field with no
    // way to bring it back). Scrolling inside the list itself is ignored.
    const reposition = (e?: Event) => {
      if (e && dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
        setLayout({ x, y, width: w, height: h });
      });
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const DROPDOWN_MAX_H = 250;
  const dropPos = computePortalPosition(layout, layout.width || 200, DROPDOWN_MAX_H);
  const portalContent = isOpen && Platform.OS === 'web' ? ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }} onClick={() => setIsOpen(false)} />
      <div ref={dropdownRef} style={{
        position: 'fixed',
        top: dropPos.top,
        left: dropPos.left,
        width: layout.width,
        zIndex: 99999,
        backgroundColor: '#FFFFFF',
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        maxHeight: 250,
        overflowY: 'auto' as any,
      }}>
        {options.map((opt) => (
          <div
            key={opt.id}
            onClick={() => { onChange(opt.id); setIsOpen(false); }}
            style={{
              padding: '10px 14px',
              cursor: 'pointer',
              textAlign: 'right',
              fontFamily: FONTS.regular,
              fontSize: 14,
              color: opt.id === value ? BRAND_RED : FOREGROUND,
              backgroundColor: opt.id === value ? '#FEF2F2' : 'transparent',
              borderBottom: `1px solid ${BORDER}`,
            }}
            onMouseEnter={(e) => { if (opt.id !== value) (e.target as any).style.backgroundColor = '#F8FAFC'; }}
            onMouseLeave={(e) => { if (opt.id !== value) (e.target as any).style.backgroundColor = 'transparent'; }}
          >
            {opt.name}
          </div>
        ))}
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <View ref={btnRef}>
      <TouchableOpacity
        onPress={handleOpen}
        style={{
          backgroundColor: '#FFFFFF',
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 10,
          paddingHorizontal: 14,
          paddingVertical: 10,
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <ChevronDownIcon size={14} color={MUTED} />
        <Text style={{ flex: 1, textAlign: 'right', fontFamily: FONTS.regular, fontSize: 14, color: value ? FOREGROUND : MUTED }}>
          {selectedLabel || placeholder || ''}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
}

/* ── FormField Component ─────────────────────────────────────── */
function FormField({
  label,
  required,
  children,
  style,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  style?: any;
}) {
  return (
    <View style={[fieldStyles.container, style]}>
      <Text style={fieldStyles.label}>
        {required && <Text style={{ color: '#D62728' }}>* </Text>}
        {label}
      </Text>
      {children}
    </View>
  );
}

/* ── File Upload Field ───────────────────────────────────────── */
function FileUploadField({
  label,
  required,
  helpText,
  fileName,
  onFileSelected,
}: {
  label: string;
  required?: boolean;
  helpText?: string;
  fileName: string;
  onFileSelected: (file: File, name: string) => void;
}) {
  const handlePress = () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/png,image/jpeg';
      input.onchange = (e: any) => {
        const file = e.target.files?.[0];
        if (file) onFileSelected(file, file.name);
      };
      input.click();
    }
  };

  return (
    <FormField label={label} required={required}>
      <TouchableOpacity
        style={uploadStyles.container}
        onPress={handlePress}
        activeOpacity={0.7}
      >
        <View style={uploadStyles.iconWrap}>
          <UploadIcon size={18} color={MUTED} />
        </View>
        <Text style={[uploadStyles.text, !fileName && { color: '#94A3B8' }]} numberOfLines={1}>
          {fileName || 'اختر ملف...'}
        </Text>
        <View style={uploadStyles.btn}>
          <Text style={uploadStyles.btnText}>تصفح</Text>
        </View>
      </TouchableOpacity>
      {!!helpText && (
        <Text style={uploadStyles.helpText}>{helpText}</Text>
      )}
    </FormField>
  );
}

/* ── Main Screen ─────────────────────────────────────────────── */
export default function EditTalentsScreen() {
  const navigation = useNavigation<NavProp>();
  const route = useRoute<RouteProp<MainStackParamList, 'EditTalents'>>();
  const mode = route.params?.mode || 'talents';
  const specId = route.params?.specId;
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { user } = useAuth();
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 20;
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);

  /* ── Section 1: Main Info ── */
  const [initiativeName, setInitiativeName] = useState('مبادرة مواهب');
  const [description, setDescription] = useState('');
  const [coverFileName, setCoverFileName] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [logoFileName, setLogoFileName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);

  /* ── Section 2: Specializations ── */
  const [specsOpen, setSpecsOpen] = useState(true);
  const [specializations, setSpecializations] = useState<SpecializationItem[]>([]);
  const [deletedSpecIds, setDeletedSpecIds] = useState<number[]>([]);

  /* ── UI State ── */
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');

  /* ── Load Data ────────────────────────────────────────────── */
  const loadData = useCallback(async () => {
    setIsLoading(true);

    if (mode === 'schedule' && specId) {
      // Schedule mode: load only the specific specialization
      try {
        const resp = await api.get(`/talent-specializations/${specId}/`);
        const raw = resp.data || resp;
        const s = raw?.data || raw; // handle nested .data if present
        const savedSchedule = s.schedule_data || [];
        setSpecializations([{
          id: s.id,
          name: s.name || '',
          description: s.description || '',
          location: s.location || '',
          startDate: s.start_date || s.date || '',
          endDate: s.end_date || '',
          budget: s.budget != null ? String(s.budget) : '',
          days: DEFAULT_DAYS.map(d => {
            const saved = savedSchedule.find((sd: any) => sd.day === d.day);
            return saved ? { ...d, selected: true, startTime: saved.startTime || '', endTime: saved.endTime || '' } : { ...d };
          }),
          department_id: s.department_id || null,
          isExpanded: true,
          isPublished: s.is_published === true,
        }]);
      } catch (e) {
        console.log('[EditTalents] load specialization failed:', e);
      }
    } else if (mode === 'add') {
      // Add mode: start with one blank expanded specialization
      setSpecializations([{ id: null, name: '', description: '', location: '', startDate: '', endDate: '', budget: '', days: DEFAULT_DAYS.map(d => ({ ...d })), department_id: null, isExpanded: true, isPublished: false }]);
    } else {
      // Info/talents mode: load all data
      try {
        const dashboard = await talentDashboardApi.get();
        if (dashboard) {
          if (dashboard.initiative_name) setInitiativeName(dashboard.initiative_name);
          if (dashboard.initiative_description) setDescription(dashboard.initiative_description);
        }
      } catch (e) {
        console.log('[EditTalents] load dashboard failed:', e);
      }

      try {
        const resp = await talentSpecializationsApi.list();
        const items: any[] = resp?.results || resp || [];
        const mapped: SpecializationItem[] = items.map((s: any) => {
          const savedSchedule = s.schedule_data || [];
          return {
            id: s.id,
            name: s.name || '',
            description: s.description || '',
            location: s.location || '',
            startDate: s.start_date || s.date || '',
            endDate: s.end_date || '',
          budget: s.budget != null ? String(s.budget) : '',
            days: DEFAULT_DAYS.map(d => {
              const saved = savedSchedule.find((sd: any) => sd.day === d.day);
              return saved ? { ...d, selected: true, startTime: saved.startTime || '', endTime: saved.endTime || '' } : { ...d };
            }),
            department_id: s.department_id || null,
            isExpanded: true,
            isPublished: s.is_published === true,
          };
        });
        setSpecializations(mapped);
      } catch (e) {
        console.log('[EditTalents] load specializations failed:', e);
      }
    }

    // Load departments for strategy team dropdown
    if (isStrategyTeam) {
      try {
        const deptResp = await api.get('/departments/');
        const depts = (deptResp.data?.results || (deptResp as any).results || []).map((d: any) => ({
          id: d.id,
          name: d.name || '',
        }));
        setDepartments(depts);
      } catch (e) {
        console.log('[EditTalents] load departments failed:', e);
      }
    }

    setIsLoading(false);
  }, [mode]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Specialization Helpers ───────────────────────────────── */
  const toggleSpec = (index: number) => {
    setSpecializations(prev =>
      prev.map((s, i) => i === index ? { ...s, isExpanded: !s.isExpanded } : s)
    );
  };

  const updateSpecField = (index: number, field: 'name' | 'description' | 'location' | 'startDate' | 'endDate' | 'budget', value: string) => {
    setSpecializations(prev =>
      prev.map((s, i) => i === index ? { ...s, [field]: value } : s)
    );
  };

  const toggleDay = (specIndex: number, dayKey: string) => {
    setSpecializations(prev => prev.map((s, i) => i === specIndex ? {
      ...s, days: s.days.map(d => d.day === dayKey ? { ...d, selected: !d.selected } : d)
    } : s));
  };

  const updateDayTime = (specIndex: number, dayKey: string, field: 'startTime' | 'endTime', value: string) => {
    setSpecializations(prev => prev.map((s, i) => i === specIndex ? {
      ...s, days: s.days.map(d => d.day === dayKey ? { ...d, [field]: value } : d)
    } : s));
  };

  const deleteSpec = (index: number) => {
    setSpecializations(prev => {
      const spec = prev[index];
      if (spec.id !== null) {
        setDeletedSpecIds(ids => [...ids, spec.id as number]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const addSpec = () => {
    const newSpec: SpecializationItem = {
      id: null,
      name: '',
      description: '',
      location: '',
      startDate: '',
      endDate: '',
      budget: '',
      days: DEFAULT_DAYS.map(d => ({ ...d })),
      department_id: null,
      isExpanded: true,
      isPublished: false,
    };
    setSpecializations(prev => [...prev, newSpec]);
  };

  /* ── Drag Reorder ──────────────────────────────────────────── */
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const reorderSpecs = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setSpecializations(prev => {
      const copy = [...prev];
      const [moved] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, moved);
      return copy;
    });
  };

  /* ── Save Handler ─────────────────────────────────────────── */
  const handleSave = async () => {
    // Initiative name is only edited/required for the strategy team
    if (isStrategyTeam && !initiativeName.trim()) {
      setToastType('error');
      setToastMsg('يرجى إدخال اسم المبادرة');
      return;
    }

    setSaving(true);
    try {
      // Initiative settings (name, description, images) are owned by the
      // strategy team — the dashboard PUT endpoint 403s for every other role.
      // Only send it for strategy_team so an org_employee publish toggle isn't
      // blocked by a forbidden pre-flight request.
      if (isStrategyTeam) {
        const formData = new FormData();
        formData.append('name', initiativeName.trim());
        formData.append('description', description.trim());
        if (coverFile) formData.append('cover_image', coverFile);
        if (logoFile) formData.append('logo_image', logoFile);
        await talentDashboardApi.updateSettings(formData);
      }

      // Save each specialization (create or update)
      for (const spec of specializations) {
        const selectedDays = spec.days.filter(d => d.selected).map(d => ({
          day: d.day,
          startTime: d.startTime || '09:00',
          endTime: d.endTime || '14:00',
        }));
        const payload: any = {
          name: spec.name.trim(),
          description: spec.description.trim(),
          location: spec.location.trim(),
          start_date: spec.startDate || null,
          end_date: spec.endDate || null,
          is_published: spec.isPublished,
        };
        // The strategy team sets only start/end dates — days and budget are the
        // entity's responsibility, so don't overwrite them from the strategy flow.
        if (!isStrategyTeam) {
          payload.schedule_data = selectedDays;
          payload.budget = spec.budget.trim() === '' ? null : parseFloat(spec.budget);
        }
        // Strategy team must specify department
        if (isStrategyTeam && spec.department_id) {
          payload.department_id = spec.department_id;
        }
        if (spec.id !== null) {
          await api.patch(`/talent-specializations/${spec.id}/`, payload);
        } else if (spec.name.trim()) {
          if (isStrategyTeam && !spec.department_id) {
            setToastType('error');
            setToastMsg(isRTL ? 'يرجى تحديد الجهة لكل موهبة' : 'Please select entity for each specialization');
            setSaving(false);
            return;
          }
          await api.post('/talent-specializations/', payload);
        }
      }

      // Delete removed specializations
      for (const id of deletedSpecIds) {
        try {
          await talentSpecializationsApi.delete(id);
        } catch (e) {
          console.log('[EditTalents] delete spec failed:', id, e);
        }
      }

      setToastType('success');
      setToastMsg('تم حفظ البيانات بنجاح');
      setTimeout(() => navigation.navigate('Talents'), 800);
    } catch (e) {
      console.log('[EditTalents] save failed:', e);
      setToastType('error');
      setToastMsg('فشل في حفظ البيانات');
    }
    setSaving(false);
  };

  /* ── Loading State ─────────────────────────────────────────── */
  if (isLoading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: MUTED }}>
            جاري التحميل...
          </Text>
        </View>
      </PageContainer>
    );
  }

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
        <View style={{ padding: containerPadding, gap: 20 }}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: 'الرئيسية' },
              { label: 'مبادرة مواهب' },
              { label: mode === 'add' ? 'إضافة موهبة جديدة' : mode === 'schedule' ? 'تعديل الجدول' : 'تعديل البيانات' },
            ]}
          />

          <View style={[cardStyles.container, createShadow(0, 1, 3, '#000', 0.08)]}>
            <Text style={cardStyles.cardTitle}>{mode === 'add' ? 'إضافة موهبة جديدة' : mode === 'schedule' ? 'تعديل الجدول' : 'تعديل البيانات'}</Text>

            {/* ── Section: Main Information (mode=info) ──── */}
            {mode === 'info' && (
              <View style={sectionStyles.container}>
                <Text style={sectionStyles.title}>المعلومات الأساسية</Text>

                {/* Name */}
                <FormField label="الاسم" required>
                  <View style={inputStyles.container}>
                    <TextInput
                      style={inputStyles.input}
                      value={initiativeName}
                      onChangeText={setInitiativeName}
                      placeholder={isRTL ? 'أدخل اسم المبادرة' : 'Enter initiative name'}
                      placeholderTextColor="#94A3B8"
                      textAlign={isRTL ? 'right' : 'left'}
                    />
                  </View>
                </FormField>

                {/* Description */}
                <FormField label="الوصف" required>
                  <View style={[inputStyles.container, { minHeight: 100 }]}>
                    <TextInput
                      style={[inputStyles.input, { height: 90, textAlignVertical: 'top' }]}
                      value={description}
                      onChangeText={setDescription}
                      placeholder={isRTL ? 'أدخل وصف المبادرة' : 'Enter initiative description'}
                      placeholderTextColor="#94A3B8"
                      multiline
                      numberOfLines={4}
                      textAlign={isRTL ? 'right' : 'left'}
                    />
                  </View>
                </FormField>

                {/* Cover Image */}
                <FileUploadField
                  label="صورة الغلاف"
                  helpText="صورة الغلاف يجب أن تكون بمقاس بين 1024*256 و 400*1600. تفضل صيغة PNG أو JPEG"
                  fileName={coverFileName}
                  onFileSelected={(file, name) => {
                    setCoverFile(file);
                    setCoverFileName(name);
                  }}
                />

                {/* Logo Image */}
                <FileUploadField
                  label="صورة الشعار"
                  helpText="صورة الشعار يجب أن تكون بمقاس بين 256*256 و 1024*1024. تفضل صيغة PNG أو JPEG"
                  fileName={logoFileName}
                  onFileSelected={(file, name) => {
                    setLogoFile(file);
                    setLogoFileName(name);
                  }}
                />
              </View>
            )}

            {/* ── Section: Specializations (mode=talents or add) ── */}
            {/* ── Section: Schedule (mode=schedule) ───── */}
            {mode === 'schedule' && specializations.length > 0 && (() => {
              const spec = specializations[0];
              return (
                <View style={sectionStyles.container}>
                  <Text style={sectionStyles.title}>{spec.name}</Text>

                  {/* Location */}
                  <FormField label={isRTL ? 'الموقع' : 'Location'}>
                    <View style={inputStyles.container}>
                      <TextInput
                        style={inputStyles.input}
                        value={spec.location}
                        onChangeText={(v) => updateSpecField(0, 'location', v)}
                        placeholder={isRTL ? 'أدخل الموقع' : 'Enter location'}
                        placeholderTextColor="#94A3B8"
                        textAlign={isRTL ? 'right' : 'left'}
                      />
                    </View>
                  </FormField>

                  {/* Date Range */}
                  <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', gap: 12 }}>
                    <View style={{ flex: 1 }}>
                      <FormField label={isRTL ? 'تاريخ البداية' : 'Start Date'}>
                        <SingleDatePicker
                          value={spec.startDate}
                          onChange={(v) => {
                            updateSpecField(0, 'startDate', v);
                            if (spec.endDate && v > spec.endDate) updateSpecField(0, 'endDate', '');
                          }}
                          maxDate={spec.endDate || undefined}
                          placeholder={isRTL ? 'اختر تاريخ البداية' : 'Select start date'}
                        />
                      </FormField>
                    </View>
                    <View style={{ flex: 1 }}>
                      <FormField label={isRTL ? 'تاريخ النهاية' : 'End Date'}>
                        <SingleDatePicker
                          value={spec.endDate}
                          onChange={(v) => updateSpecField(0, 'endDate', v)}
                          minDate={spec.startDate || undefined}
                          placeholder={isRTL ? 'اختر تاريخ النهاية' : 'Select end date'}
                        />
                      </FormField>
                    </View>
                  </View>

                  {/* Days Selection */}
                  {spec.startDate && spec.endDate && (() => {
                    const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                    const start = new Date(spec.startDate + 'T00:00:00');
                    const end = new Date(spec.endDate + 'T00:00:00');
                    const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                    const availableDays = new Set<string>();
                    if (diffDays >= 6) { DAY_KEYS.forEach(k => availableDays.add(k)); }
                    else { for (let i = 0; i <= diffDays; i++) { const d = new Date(start); d.setDate(d.getDate() + i); availableDays.add(DAY_KEYS[d.getDay()]); } }
                    const filteredDays = spec.days.filter(d => availableDays.has(d.day));
                    return (
                      <View style={{ gap: 16 }}>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                          {filteredDays.map((d) => (
                            <TouchableOpacity key={d.day} onPress={() => toggleDay(0, d.day)}
                              style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, borderColor: d.selected ? FOREGROUND : BORDER, backgroundColor: d.selected ? FOREGROUND : '#FFFFFF' }}>
                              <Text style={{ fontSize: 13, fontFamily: FONTS.medium || FONTS.regular, color: d.selected ? '#FFFFFF' : MUTED }}>{isRTL ? d.labelAr : d.labelEn}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                        {filteredDays.filter(d => d.selected).map((d) => (
                          <View key={d.day} style={{ backgroundColor: SECTION_BG, borderRadius: 8, padding: 12, gap: 6 }}>
                            <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 12 }}>
                              <View style={{ backgroundColor: FOREGROUND, borderRadius: 8, paddingHorizontal: 14, paddingVertical: 8, alignSelf: 'center' }}>
                                <Text style={{ fontSize: 13, fontFamily: FONTS.medium || FONTS.regular, color: '#FFFFFF' }}>{isRTL ? d.labelAr : d.labelEn}</Text>
                              </View>
                              <View style={{ flex: 1, gap: 4 }}>
                                <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: MUTED, textAlign: 'right' }}><Text style={{ color: '#D62728' }}>* </Text>{isRTL ? 'وقت البداية' : 'Start Time'}</Text>
                                <View style={{ backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: BORDER, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 }}>
                                  {Platform.OS === 'web' ? <TimePicker12h value={d.startTime} onChange={(v) => updateDayTime(0, d.day, 'startTime', v)} /> : <TextInput style={inputStyles.input} value={d.startTime} placeholder="HH:MM" placeholderTextColor="#94A3B8" />}
                                </View>
                              </View>
                              <View style={{ flex: 1, gap: 4 }}>
                                <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: MUTED, textAlign: 'right' }}><Text style={{ color: '#D62728' }}>* </Text>{isRTL ? 'وقت النهاية' : 'End Time'}</Text>
                                <View style={{ backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: BORDER, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 }}>
                                  {Platform.OS === 'web' ? <TimePicker12h value={d.endTime} onChange={(v) => updateDayTime(0, d.day, 'endTime', v)} /> : <TextInput style={inputStyles.input} value={d.endTime} placeholder="HH:MM" placeholderTextColor="#94A3B8" />}
                                </View>
                              </View>
                            </View>
                          </View>
                        ))}
                      </View>
                    );
                  })()}

                  {/* Publish Toggle */}
                  <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#E2E8F0' }}>
                    <Text style={{ fontFamily: FONTS.medium || FONTS.regular, fontSize: 14, color: FOREGROUND, textAlign: isRTL ? 'right' : 'left' }}>
                      {spec.isPublished ? 'منشور (يظهر في صفحة التسجيل)' : 'غير منشور (بانتظار النشر)'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setSpecializations(prev => prev.map((s, i) => i === 0 ? { ...s, isPublished: !s.isPublished } : s))}
                      activeOpacity={0.7}
                      style={{
                        width: 48,
                        height: 26,
                        borderRadius: 13,
                        backgroundColor: spec.isPublished ? '#22C55E' : '#D1D5DB',
                        justifyContent: 'center',
                        padding: 2,
                      }}
                    >
                      <View style={{
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        backgroundColor: '#FFFFFF',
                        alignSelf: spec.isPublished ? 'flex-end' : 'flex-start',
                      }} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })()}

            {(mode === 'talents' || mode === 'add') && (
              <View style={sectionStyles.container}>
                {/* Section Header (collapsible) */}
                <TouchableOpacity
                  style={[sectionStyles.header, { flexDirection: isRTL ? 'row' : 'row-reverse' }]}
                  onPress={() => setSpecsOpen(v => !v)}
                  activeOpacity={0.7}
                >
                  {specsOpen ? (
                    <ChevronUpIcon size={18} color={FOREGROUND} />
                  ) : (
                    <ChevronDownIcon size={18} color={FOREGROUND} />
                  )}
                  <Text style={sectionStyles.title}>المواهب</Text>
                </TouchableOpacity>

                {specsOpen && (
                  <View style={{ gap: 12 }}>
                    {specializations.map((spec, specIndex) => (
                      <View
                        key={spec.id !== null ? `spec_${spec.id}` : `new_${specIndex}`}
                        {...(Platform.OS === 'web' ? {
                          draggable: false,
                          onDragOver: (e: any) => { e.preventDefault(); setDragOverIndex(specIndex); },
                          onDrop: (e: any) => { e.preventDefault(); if (dragIndex !== null) { reorderSpecs(dragIndex, specIndex); } setDragIndex(null); setDragOverIndex(null); },
                        } : {})}
                        style={[
                          dragOverIndex === specIndex && dragIndex !== null && dragIndex !== specIndex
                            ? { borderTopWidth: 2, borderTopColor: BRAND_RED, borderRadius: 8 }
                            : undefined,
                        ]}
                      >
                        <SpecializationAccordion
                          spec={spec}
                          index={specIndex}
                          isRTL={isRTL}
                          isStrategyTeam={isStrategyTeam}
                          departments={departments}
                          onToggle={() => toggleSpec(specIndex)}
                          onUpdateField={(field, value) => updateSpecField(specIndex, field, value)}
                          onToggleDay={(dayKey) => toggleDay(specIndex, dayKey)}
                          onUpdateDayTime={(dayKey, field, value) => updateDayTime(specIndex, dayKey, field, value)}
                          onUpdateDept={(deptId) => setSpecializations(prev => prev.map((s, i) => i === specIndex ? { ...s, department_id: deptId } : s))}
                          onTogglePublish={() => setSpecializations(prev => prev.map((s, i) => i === specIndex ? { ...s, isPublished: !s.isPublished } : s))}
                          onDelete={() => deleteSpec(specIndex)}
                          onDragStart={() => setDragIndex(specIndex)}
                          onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                          isDragging={dragIndex === specIndex}
                          onMoveUp={() => reorderSpecs(specIndex, specIndex - 1)}
                          onMoveDown={() => reorderSpecs(specIndex, specIndex + 1)}
                          isFirst={specIndex === 0}
                          isLast={specIndex === specializations.length - 1}
                        />
                      </View>
                    ))}

                    {/* Add Specialization Button (hidden in 'add' mode and for strategy_team) */}
                    {mode !== 'add' && !isStrategyTeam && (
                      <TouchableOpacity
                        style={addBtnStyles.container}
                        onPress={addSpec}
                        activeOpacity={0.7}
                      >
                        <Text style={addBtnStyles.text}>+ إضافة موهبة</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            )}

            {/* ── Footer Buttons ─────────────────────────── */}
            <View style={[footerStyles.container, { flexDirection: 'row' }]}>
              <TouchableOpacity
                style={[footerStyles.saveBtn, saving && { opacity: 0.6 }]}
                onPress={handleSave}
                disabled={saving}
                activeOpacity={0.8}
              >
                <Text style={footerStyles.saveBtnText}>
                  {saving ? 'جاري الحفظ...' : 'حفظ'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={footerStyles.cancelBtn}
                onPress={() => navigation.navigate('Talents')}
                activeOpacity={0.7}
              >
                <Text style={footerStyles.cancelBtnText}>إلغاء</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>

      <Toast
        visible={!!toastMsg}
        message={toastMsg}
        type={toastType}
        onHide={() => { setToastMsg(''); setToastType('info'); }}
      />
    </PageContainer>
  );
}

/* ── Specialization Accordion Component ─────────────────────── */
interface SpecAccordionProps {
  spec: SpecializationItem;
  index: number;
  isRTL: boolean;
  onToggle: () => void;
  onUpdateField: (field: 'name' | 'description' | 'location' | 'startDate' | 'endDate' | 'budget', value: string) => void;
  onToggleDay: (dayKey: string) => void;
  onUpdateDayTime: (dayKey: string, field: 'startTime' | 'endTime', value: string) => void;
  onTogglePublish: () => void;
  onDelete: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

function WebDragHandle({ index, onDragStart, onDragEnd }: { index: number; onDragStart?: () => void; onDragEnd?: () => void }) {
  const ref = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || !ref.current) return;
    const el = ref.current as any;
    // Access the underlying DOM node
    const node = el instanceof HTMLElement ? el : el._nativeTag || el;
    if (node instanceof HTMLElement) {
      node.draggable = true;
      node.style.cursor = 'grab';
      const handleDragStart = (e: DragEvent) => {
        e.dataTransfer?.setData('text/plain', String(index));
        onDragStart?.();
      };
      const handleDragEnd = () => onDragEnd?.();
      node.addEventListener('dragstart', handleDragStart);
      node.addEventListener('dragend', handleDragEnd);
      return () => {
        node.removeEventListener('dragstart', handleDragStart);
        node.removeEventListener('dragend', handleDragEnd);
      };
    }
  }, [index, onDragStart, onDragEnd]);

  return (
    <View ref={ref} style={accordionStyles.dragHandle}>
      <DragHandleIcon size={16} color={MUTED} />
    </View>
  );
}

function SpecializationAccordion({
  spec,
  index,
  isRTL,
  isStrategyTeam,
  departments,
  onToggle,
  onUpdateField,
  onToggleDay,
  onUpdateDayTime,
  onTogglePublish,
  onUpdateDept,
  onDelete,
  onDragStart,
  onDragEnd,
  isDragging,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: SpecAccordionProps & { isStrategyTeam?: boolean; departments?: { id: number; name: string }[]; onUpdateDept?: (deptId: number | null) => void; onToggleDay: (dayKey: string) => void; onUpdateDayTime: (dayKey: string, field: 'startTime' | 'endTime', value: string) => void; onTogglePublish: () => void }) {
  return (
    <View style={[accordionStyles.container, isDragging && { opacity: 0.5 }]}>
      {/* Header: RIGHT = number + ≡ handle, middle = name, LEFT = chevron + trash */}
      <View style={[accordionStyles.header, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        {/* Right side in RTL: number badge + drag handle */}
        <View style={accordionStyles.badge}>
          <Text style={accordionStyles.badgeText}>{index + 1}</Text>
        </View>
        <WebDragHandle index={index} onDragStart={onDragStart} onDragEnd={onDragEnd} />

        {/* Name */}
        <Text style={[accordionStyles.name, { flex: 1, textAlign: isRTL ? 'right' : 'left' }]} numberOfLines={1}>
          {spec.name || `الموهبة ${index + 1}`}
        </Text>

        {/* Left side in RTL: collapse chevron + trash */}
        <TouchableOpacity
          style={accordionStyles.iconBtn}
          onPress={onToggle}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          {spec.isExpanded ? (
            <ChevronUpIcon size={16} color={FOREGROUND} />
          ) : (
            <ChevronDownIcon size={16} color={FOREGROUND} />
          )}
        </TouchableOpacity>
        <TouchableOpacity
          style={accordionStyles.iconBtn}
          onPress={onDelete}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <TrashIcon size={16} color={MUTED} />
        </TouchableOpacity>
      </View>

      {/* Accordion Body */}
      {spec.isExpanded && (
        <View style={accordionStyles.body}>
          <View style={sectionStyles.container}>
            <Text style={sectionStyles.title}>المعلومات الأساسية</Text>

            {/* Spec Name */}
            <FormField label="اسم الموهبة" required>
              <View style={inputStyles.container}>
                <TextInput
                  style={inputStyles.input}
                  value={spec.name}
                  onChangeText={(v) => onUpdateField('name', v)}
                  placeholder={isRTL ? 'أدخل اسم الموهبة' : 'Enter talent name'}
                  placeholderTextColor="#94A3B8"
                  textAlign={isRTL ? 'right' : 'left'}
                />
              </View>
            </FormField>

            {/* Spec Description */}
            <FormField label="الوصف" required>
              <View style={[inputStyles.container, { minHeight: 80 }]}>
                <TextInput
                  style={[inputStyles.input, { height: 70, textAlignVertical: 'top' }]}
                  value={spec.description}
                  onChangeText={(v) => onUpdateField('description', v)}
                  placeholder={isRTL ? 'أدخل وصف الموهبة' : 'Enter talent description'}
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={3}
                  textAlign={isRTL ? 'right' : 'left'}
                />
              </View>
            </FormField>

            {/* Department Dropdown — Strategy Team only */}
            {isStrategyTeam && (
              <FormField label={isRTL ? 'الجهة' : 'Entity'} required>
                <CustomDropdown
                  value={spec.department_id}
                  options={departments || []}
                  onChange={(id) => onUpdateDept?.(id)}
                  placeholder={isRTL ? 'اختر الجهة' : 'Select Entity'}
                />
              </FormField>
            )}

            {/* Location */}
            <FormField label={isRTL ? 'الموقع' : 'Location'}>
              <View style={inputStyles.container}>
                <TextInput
                  style={inputStyles.input}
                  value={spec.location}
                  onChangeText={(v) => onUpdateField('location', v)}
                  placeholder={isRTL ? 'أدخل الموقع' : 'Enter location'}
                  placeholderTextColor="#94A3B8"
                  textAlign={isRTL ? 'right' : 'left'}
                />
              </View>
            </FormField>

            {/* Budget — set by the entity (org employee) only */}
            {!isStrategyTeam && (
              <FormField label={isRTL ? 'الميزانية' : 'Budget'}>
                <View style={inputStyles.container}>
                  <TextInput
                    style={inputStyles.input}
                    value={spec.budget}
                    onChangeText={(v) => onUpdateField('budget', normalizeArabicNumbers(v))}
                    placeholder={isRTL ? 'أدخل الميزانية' : 'Enter budget'}
                    placeholderTextColor="#94A3B8"
                    keyboardType="numeric"
                    textAlign={isRTL ? 'right' : 'left'}
                  />
                </View>
              </FormField>
            )}

            {/* Date Range */}
            <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', gap: 12 }}>
              <View style={{ flex: 1 }}>
                <FormField label={isRTL ? 'تاريخ البداية' : 'Start Date'}>
                  <SingleDatePicker
                    value={spec.startDate}
                    onChange={(v) => {
                      onUpdateField('startDate', v);
                      if (spec.endDate && v > spec.endDate) onUpdateField('endDate', '');
                    }}
                    maxDate={spec.endDate || undefined}
                    placeholder={isRTL ? 'اختر تاريخ البداية' : 'Select start date'}
                  />
                </FormField>
              </View>
              <View style={{ flex: 1 }}>
                <FormField label={isRTL ? 'تاريخ النهاية' : 'End Date'}>
                  <SingleDatePicker
                    value={spec.endDate}
                    onChange={(v) => onUpdateField('endDate', v)}
                    minDate={spec.startDate || undefined}
                    placeholder={isRTL ? 'اختر تاريخ النهاية' : 'Select end date'}
                  />
                </FormField>
              </View>
            </View>

            {/* Days Selection — the entity (org employee) sets the days per its
                activities; the strategy team only sets start/end dates. */}
            {!isStrategyTeam && spec.startDate && spec.endDate && (() => {
              // Calculate which days of the week fall within the date range
              const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
              const start = new Date(spec.startDate + 'T00:00:00');
              const end = new Date(spec.endDate + 'T00:00:00');
              const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
              const availableDays = new Set<string>();
              // If range >= 7 days, all days are available
              if (diffDays >= 6) {
                DAY_KEYS.forEach(k => availableDays.add(k));
              } else {
                for (let i = 0; i <= diffDays; i++) {
                  const d = new Date(start);
                  d.setDate(d.getDate() + i);
                  availableDays.add(DAY_KEYS[d.getDay()]);
                }
              }
              const filteredDays = spec.days.filter(d => availableDays.has(d.day));
              return (
              <View style={{ gap: 16 }}>
                {/* Day chips — dark pill style */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                  {filteredDays.map((d) => (
                    <TouchableOpacity
                      key={d.day}
                      onPress={() => onToggleDay(d.day)}
                      style={{
                        paddingHorizontal: 16,
                        paddingVertical: 8,
                        borderRadius: 20,
                        borderWidth: 1,
                        borderColor: d.selected ? FOREGROUND : BORDER,
                        backgroundColor: d.selected ? FOREGROUND : '#FFFFFF',
                      }}
                    >
                      <Text style={{
                        fontSize: 13,
                        fontFamily: FONTS.medium || FONTS.regular,
                        color: d.selected ? '#FFFFFF' : MUTED,
                      }}>
                        {isRTL ? d.labelAr : d.labelEn}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {/* Per-day time rows for selected days */}
                {filteredDays.filter(d => d.selected).map((d) => (
                  <View key={d.day} style={{ backgroundColor: SECTION_BG, borderRadius: 8, padding: 12, gap: 6 }}>
                    {/* Row: day badge + time labels */}
                    <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 12 }}>
                      {/* Day badge */}
                      <View style={{ backgroundColor: FOREGROUND, borderRadius: 8, paddingHorizontal: 14, paddingVertical: 8, alignSelf: 'center' }}>
                        <Text style={{ fontSize: 13, fontFamily: FONTS.medium || FONTS.regular, color: '#FFFFFF' }}>
                          {isRTL ? d.labelAr : d.labelEn}
                        </Text>
                      </View>
                      {/* Start time */}
                      <View style={{ flex: 1, gap: 4 }}>
                        <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: MUTED, textAlign: 'right' }}>
                          <Text style={{ color: '#D62728' }}>* </Text>
                          {isRTL ? 'وقت البداية' : 'Start Time'}
                        </Text>
                        <View style={{ backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: BORDER, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 }}>
                          {Platform.OS === 'web' ? (
                            <TimePicker12h value={d.startTime} onChange={(v) => onUpdateDayTime(d.day, 'startTime', v)} />
                          ) : (
                            <TextInput style={inputStyles.input} value={d.startTime} placeholder="HH:MM" placeholderTextColor="#94A3B8" />
                          )}
                        </View>
                      </View>
                      {/* End time */}
                      <View style={{ flex: 1, gap: 4 }}>
                        <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: MUTED, textAlign: 'right' }}>
                          <Text style={{ color: '#D62728' }}>* </Text>
                          {isRTL ? 'وقت النهاية' : 'End Time'}
                        </Text>
                        <View style={{ backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: BORDER, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 }}>
                          {Platform.OS === 'web' ? (
                            <TimePicker12h value={d.endTime} onChange={(v) => onUpdateDayTime(d.day, 'endTime', v)} />
                          ) : (
                            <TextInput style={inputStyles.input} value={d.endTime} placeholder="HH:MM" placeholderTextColor="#94A3B8" />
                          )}
                        </View>
                      </View>
                    </View>
                  </View>
                ))}
              </View>
              );
            })()}

          </View>

          {/* Publish Toggle — only org_employee can publish */}
          {!isStrategyTeam && <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#E2E8F0' }}>
            <Text style={{ fontFamily: 'JF-Flat-Medium', fontSize: 14, color: FOREGROUND, textAlign: isRTL ? 'right' : 'left' }}>
              {spec.isPublished ? 'منشور (يظهر في صفحة التسجيل)' : 'غير منشور (بانتظار النشر)'}
            </Text>
            <TouchableOpacity
              onPress={onTogglePublish}
              activeOpacity={0.7}
              style={{
                width: 48,
                height: 26,
                borderRadius: 13,
                backgroundColor: spec.isPublished ? '#22C55E' : '#D1D5DB',
                justifyContent: 'center',
                padding: 2,
              }}
            >
              <View style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                backgroundColor: '#FFFFFF',
                alignSelf: spec.isPublished ? 'flex-end' : 'flex-start',
              }} />
            </TouchableOpacity>
          </View>}
        </View>
      )}
    </View>
  );
}

/* ================================================================ */
/*  Styles                                                           */
/* ================================================================ */

const cardStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    padding: 20,
    gap: 20,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FONTS.bold || FONTS.medium,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const sectionStyles = StyleSheet.create({
  container: {
    backgroundColor: SECTION_BG,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    padding: 20,
    gap: 16,
  },
  header: {
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 15,
    fontFamily: FONTS.bold || FONTS.medium,
    color: FOREGROUND,
    textAlign: 'right',
    flex: 1,
  },
  subTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium || FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const fieldStyles = StyleSheet.create({
  container: {
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
});

const inputStyles = StyleSheet.create({
  container: {
    backgroundColor: INPUT_BG,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 40,
  },
  input: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
    padding: 0,
    margin: 0,
  },
});

const uploadStyles = StyleSheet.create({
  container: {
    backgroundColor: INPUT_BG,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 44,
  },
  iconWrap: {
    width: 24,
    alignItems: 'center',
  },
  text: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  btn: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  btnText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  helpText: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: MUTED,
    textAlign: 'right',
    marginTop: 4,
    lineHeight: 16,
  },
});

const accordionStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 0,
  },
  badge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: FOREGROUND,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 12,
    fontFamily: FONTS.bold || FONTS.medium,
    color: '#FFFFFF',
  },
  name: {
    fontSize: 14,
    fontFamily: FONTS.medium || FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  controls: {
    alignItems: 'center',
    gap: 8,
  },
  iconBtn: {
    padding: 4,
  },
  dragHandle: {
    padding: 4,
  },
  dragGroup: {
    alignItems: 'center',
    gap: 2,
  },
  moveBtn: {
    padding: 2,
  },
  body: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: BORDER,
  },
});

const entityStyles = StyleSheet.create({
  headerRow: {
    alignItems: 'center',
    gap: 8,
    marginBottom: -4,
  },
  colLabel: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  container: {
    alignItems: 'center',
    gap: 8,
  },
  dragHandle: {
    padding: 4,
  },
  dropdownInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    alignItems: 'center',
    gap: 8,
  },
  dropdownText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    padding: 0,
    margin: 0,
  },
  weightBox: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    width: 120,
    alignItems: 'center',
    gap: 4,
  },
  weightInput: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    padding: 0,
    margin: 0,
  },
  percentLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: MUTED,
  },
  deleteBtn: {
    padding: 6,
  },
});

const addBtnStyles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: BRAND_RED,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF5F5',
  },
  text: {
    fontSize: 14,
    fontFamily: FONTS.medium || FONTS.regular,
    color: BRAND_RED,
    textAlign: 'center',
  },
  entityContainer: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: BORDER,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  entityText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: BRAND_RED,
    textAlign: 'center',
  },
});

const footerStyles = StyleSheet.create({
  container: {
    alignItems: 'center',
    gap: 8,
    paddingTop: 8,
  },
  saveBtn: {
    backgroundColor: BRAND_RED,
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 9,
    minHeight: 38,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold || FONTS.medium,
    color: '#FFFFFF',
  },
  cancelBtn: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 9,
    minHeight: 38,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold || FONTS.medium,
    color: FOREGROUND,
  },
});

const dpStyles = StyleSheet.create({
  button: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  buttonText: { fontSize: 14, fontFamily: FONTS.regular, flex: 1, textAlign: 'right' },
  panel: { width: 320, borderRadius: 12, borderWidth: 1, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10 },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  navBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  monthLabel: { fontSize: 16, fontFamily: FONTS.medium },
  dayHeaderRow: { flexDirection: 'row', marginBottom: 8 },
  dayHeaderText: { flex: 1, textAlign: 'center', fontSize: 12, fontFamily: FONTS.medium },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '14.28%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  cellText: { fontSize: 14, fontFamily: FONTS.regular },
});
